export type CabinetPlatform = "telegram" | "webxdc";
export type PaymentProvider = "platega" | "cryptomus";
export type SurfaceKind = "screen" | "dialog" | "sheet" | "handoff";
export type SurfaceBlockKind = "text" | "status" | "code" | "list" | "notice";
export type SurfaceActionKind =
  | "navigate"
  | "choose"
  | "submit"
  | "open_url"
  | "copy_value"
  | "handoff"
  | "external_checkout"
  | "refresh"
  | "back";

export interface AppBootstrap {
  platform: CabinetPlatform;
  appVersion: string;
  protocolVersion: number;
  supportEnabled: boolean;
  paymentsEnabled: boolean;
}

export interface AccountStatus {
  hasActiveSubscription: boolean;
  planLabel: string | null;
  country: string | null;
  expiresAtLabel: string | null;
  accessText: string | null;
}

export interface SurfaceBlock {
  blockId: string;
  kind: SurfaceBlockKind;
  title: string | null;
  body: string | null;
  items: string[];
}

export interface SurfaceAction {
  actionId: string;
  label: string;
  kind: SurfaceActionKind;
  payload: Record<string, unknown>;
}

export interface SurfaceModel {
  surfaceId: string;
  surfaceKind: SurfaceKind;
  title: string;
  blocks: SurfaceBlock[];
  actions: SurfaceAction[];
  notices: string[];
  metadata: Record<string, unknown>;
}

export interface DispatchSurfaceActionInput {
  actionId: string;
  payload?: Record<string, unknown>;
}

export interface Tariff {
  id: string;
  title: string;
  description: string;
  priceLabel: string;
  availableProviders: PaymentProvider[];
}

export interface CreatePaymentInput {
  planId: string;
  provider: PaymentProvider;
}

export interface PaymentResult {
  paymentId: string;
  provider: PaymentProvider;
  paymentUrl: string;
  fallbackText: string;
}

export interface InstructionBlock {
  title?: string | null;
  body: string;
}

export interface Instruction {
  title: string;
  blocks: InstructionBlock[];
}

export interface SupportLink {
  label: string;
  url: string;
}

export interface ClientLogEvent {
  event: string;
  details: Record<string, unknown>;
}

export interface AppBridge {
  bootstrap(): Promise<AppBootstrap>;
  getAccountStatus(): Promise<AccountStatus>;
  getSurface(surfaceId: string): Promise<SurfaceModel>;
  dispatchAction(input: DispatchSurfaceActionInput): Promise<SurfaceModel>;
  getTariffs(): Promise<Tariff[]>;
  createPayment(input: CreatePaymentInput): Promise<PaymentResult>;
  getInstruction(): Promise<Instruction>;
  getSupportLink(): Promise<SupportLink>;
  logClientEvent(event: ClientLogEvent): Promise<void>;
}

export class MockBridge implements AppBridge {
  constructor(private readonly platform: CabinetPlatform = "telegram") {}

  async bootstrap(): Promise<AppBootstrap> {
    return {
      platform: this.platform,
      appVersion: "mock",
      protocolVersion: 1,
      supportEnabled: true,
      paymentsEnabled: false,
    };
  }

  async getAccountStatus(): Promise<AccountStatus> {
    return {
      hasActiveSubscription: true,
      planLabel: "30 ГБ",
      country: "Netherlands",
      expiresAtLabel: "14 April, 2026",
      accessText: "vless://example",
    };
  }

  async getSurface(_surfaceId: string): Promise<SurfaceModel> {
    return {
      surfaceId: "subscription",
      surfaceKind: "screen",
      title: "",
      blocks: [
        {
          blockId: "subscription-overview",
          kind: "text",
          title: null,
          body: "⚡️ Ваш тарифный план: 30 ГБ\n⏳ Действует до: 14 April, 2026\n📱 Ключ можно использовать на 5 устройствах одновременно.",
          items: [],
        },
        {
          blockId: "subscription-access",
          kind: "code",
          title: null,
          body: "🔑 Ваш ключ VLESS Reality:\n\nvless://example",
          items: [],
        },
      ],
      actions: [
        {
          actionId: "subscription.show_qr",
          label: "QR code VLESS Reality",
          kind: "navigate",
          payload: {
            qr: {
              title: "QR code VLESS Reality",
              value: "vless://example",
            },
          },
        },
      ],
      notices: [],
      metadata: {},
    };
  }

  async dispatchAction(input: DispatchSurfaceActionInput): Promise<SurfaceModel> {
    const surface = await this.getSurface("subscription");
    return {
      ...surface,
      notices: [`Выполнено: ${input.actionId}`],
    };
  }

  async getTariffs(): Promise<Tariff[]> {
    return [
      {
        id: "30gb",
        title: "VLESS | 30 ГБ",
        description: "30 дней",
        priceLabel: "300 ₽",
        availableProviders: ["platega", "cryptomus"],
      },
      {
        id: "unlimited",
        title: "VLESS | Безлимит",
        description: "30 дней",
        priceLabel: "500 ₽",
        availableProviders: ["platega", "cryptomus"],
      },
    ];
  }

  async createPayment(input: CreatePaymentInput): Promise<PaymentResult> {
    return {
      paymentId: `mock-${input.planId}-${input.provider}`,
      provider: input.provider,
      paymentUrl: `https://pay.example.test/${input.provider}/${input.planId}`,
      fallbackText: "Ссылка для оплаты также будет отправлена сообщением.",
    };
  }

  async getInstruction(): Promise<Instruction> {
    return {
      title: "Инструкция",
      blocks: [
        {
          body: "Скачайте приложение Hiddify, скопируйте ключ и добавьте его в приложение.",
        },
        {
          title: "Ссылки",
          body: "Как подключить: https://paxvpn.com/connect/",
        },
      ],
    };
  }

  async getSupportLink(): Promise<SupportLink> {
    return {
      label: "Открыть чат поддержки",
      url: "https://t.me/paxvpn_support",
    };
  }

  async logClientEvent(_event: ClientLogEvent): Promise<void> {}
}
